import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { faChevronRight, faFlask, faLayerGroup, faPlus, faSearch } from '@fortawesome/free-solid-svg-icons';
import { ColumnModel, FilterSettingsModel, GridComponent, PageSettingsModel, RowSelectEventArgs } from '@syncfusion/ej2-angular-grids';
import { ErrorService } from '../../../../../@core/utils/error.service';
import { TenantWordsPipe } from '../../../../core/pipes/tenant-words.pipe';
import { InventoryService } from '../../../pick-plan/services/inventory.service';
import { InventoryItemAssemblyDTO } from '../../models/item-assembly';
import { ItemAssemblyEditComponent } from '../item-assembly-edit/item-assembly-edit.component';

const MOBILE_PAGE_SIZE = 25;

/** Flat grid row so search, filters and sorting work on plain fields. */
interface AssemblyRow {
  assembly: InventoryItemAssemblyDTO;
  id?: number;
  name: string;
  createdItemName: string;
  description: string;
  instructions: string;
  estCompletedQuantity?: number;
  componentCount: number;
}

@Component({
  selector: 'item-assemblies',
  templateUrl: './item-assemblies.component.html',
  styleUrls: ['./item-assemblies.component.scss']
})
export class ItemAssembliesComponent implements OnInit {
  @ViewChild('countTemplate', { static: true }) public countTemplate: any;
  @ViewChild('actionTemplate', { static: true }) public actionTemplate: any;
  @ViewChild('grid', { static: false }) grid: GridComponent;

  public faChevronRight = faChevronRight;
  public faFlask = faFlask;
  public faLayerGroup = faLayerGroup;
  public faPlus = faPlus;
  public faSearch = faSearch;

  public isLoading = true;
  public searchTerm = '';
  public rows: AssemblyRow[] = [];
  private allRows: AssemblyRow[] = [];
  public columns: ColumnModel[] = [];
  public mobileVisibleCount = MOBILE_PAGE_SIZE;

  public pageSettings: PageSettingsModel = { pageSize: 50, pageSizes: [50, 100, 200] };
  public filterSettings: FilterSettingsModel = { mode: 'Immediate', type: 'Excel' };

  private destroyRef = inject(DestroyRef);

  constructor(
    private dialog: MatDialog,
    private errorService: ErrorService,
    private inventoryService: InventoryService,
    private tenantWordPipe: TenantWordsPipe
  ) { }

  ngOnInit() {
    this.setColumns();
    this.getData();
  }

  getData() {
    this.isLoading = true;
    this.inventoryService.getInventoryItemAssemblies().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: assemblies => {
        this.allRows = (assemblies ?? []).map(assembly => this.toRow(assembly));
        this.applyFilters();
        this.isLoading = false;
      }, error: err => {
        this.errorService.handleError(err);
        this.isLoading = false;
      }
    });
  }

  applyFilters() {
    const term = this.searchTerm.trim().toLowerCase();
    this.rows = term
      ? this.allRows.filter(row => [row.name, row.createdItemName, row.description, row.instructions]
        .some(value => value.toLowerCase().includes(term)))
      : [...this.allRows];
    this.mobileVisibleCount = MOBILE_PAGE_SIZE;
  }

  onDataBound() {
    // Instructions stays fixed-width and clipped; the tooltip and the dialog show it in full.
    this.grid?.autoFitColumns(this.columns.map(column => column.field).filter(field => field && field !== 'instructions'));
  }

  onRowSelected(args: RowSelectEventArgs) {
    const row = args?.data as AssemblyRow;
    if (row) this.openEdit(row.assembly);
  }

  openEdit(itemAssembly?: InventoryItemAssemblyDTO) {
    ItemAssemblyEditComponent.open(this.dialog, { itemAssembly })
      .afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(changed => {
        this.grid?.clearSelection();
        if (changed)
          this.getData();
      });
  }

  showMoreMobile() {
    this.mobileVisibleCount += MOBILE_PAGE_SIZE;
  }

  trackByRow(_: number, row: AssemblyRow): number | undefined {
    return row.id;
  }

  private toRow(assembly: InventoryItemAssemblyDTO): AssemblyRow {
    return {
      assembly,
      id: assembly.id,
      name: assembly.name ?? '',
      createdItemName: assembly.createdItem?.name ?? '',
      description: assembly.description ?? '',
      instructions: assembly.instructions ?? '',
      estCompletedQuantity: assembly.estCompletedQuantity,
      componentCount: assembly.components?.length ?? 0
    };
  }

  private setColumns() {
    this.columns = [
      { field: 'name', headerText: 'Name', autoFit: true, clipMode: 'EllipsisWithTooltip' },
      { field: 'createdItemName', headerText: `Created ${this.tenantWordPipe.transform('Product')}`, autoFit: true, clipMode: 'EllipsisWithTooltip' },
      { field: 'description', headerText: 'Distinguished By', autoFit: true, clipMode: 'EllipsisWithTooltip' },
      { field: 'instructions', headerText: 'Instructions', width: 320, clipMode: 'EllipsisWithTooltip' },
      { field: 'componentCount', headerText: 'Constituents', autoFit: true, textAlign: 'Right', template: this.countTemplate },
      { field: 'estCompletedQuantity', headerText: 'Est. Completed', autoFit: true, format: 'N0', textAlign: 'Right' },
      { headerText: '', template: this.actionTemplate, width: 56, allowSorting: false, allowFiltering: false, textAlign: 'Center' }
    ];
  }
}
